import React from "react";

import { Divider, Grid } from "@material-ui/core";
import { ArrayHelpers, FieldArray, useFormikContext } from "formik";
import SlimButton, { SlimSecondaryButton } from "components/Button/SlimButton";
import { Input } from "components/Form/FormikControls";
import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { Row } from "components/Layout";

import { PricingTypeId } from "constants/pricingTypes";

import { calculateTotalPriceCents, formatDecimal } from "lib";

import { useIsMobile, useResolveSkinsSexId } from "hooks";

import { faTrash, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { SectionDivider } from "components/ReviewCard";
import { IconButtonLabel } from "components/MultiSaleLotsForm";

interface SkinsLot extends SaleLot {
  unitPrice?: number | string | null;
}

interface SkinsFormContext {
  skinsLots: SkinsLot[];
}

export interface MultiSkinsFormProps {
  addLineText?: string;
  handleClickDelete: (skinsLot: SkinsLot, index: number) => void;
  speciesId?: number | null;
}

const addSkinsLot = (
  arrayHelpers: ArrayHelpers,
  skinsLots: SkinsLot[],
  skinsSexId: number,
) => {
  arrayHelpers.push({
    ...skinsLots[skinsLots.length - 1],
    quantity: "",
    unitPrice: "",
    total_mass_grams: 0,
    pricing_type_id: PricingTypeId.PER_HEAD,
    sex_id: skinsSexId,
    id: undefined,
  });
};

export const MultiSkinsForm = ({
  addLineText = "Add line",
  handleClickDelete,
  speciesId = null,
}: MultiSkinsFormProps) => {
  const { values } = useFormikContext<SkinsFormContext>();
  const { skinsLots } = values;
  const isMobile = useIsMobile();

  const skinsSexId = useResolveSkinsSexId(speciesId);

  return (
    <FieldArray name="skinsLots">
      {arrayHelpers => (
        <>
          {skinsLots.map((skinsLot, i) => {
            const skinValue =
              calculateTotalPriceCents({
                ...skinsLot,
                unitPrice: skinsLot.unitPrice ?? 0,
              }) || 0;
            const showLabel = i === 0 || isMobile;
            const isLast = i === skinsLots.length - 1;

            const showDelete = !(i === 0 && !skinsLot.id);

            return (
              <React.Fragment key={skinsLot.id || i}>
                {isMobile && (
                  <SectionDivider>
                    Skins{" "}
                    {skinsLot?.lot_number
                      ? `(Lot ${skinsLot.lot_number})`
                      : i + 1}
                  </SectionDivider>
                )}
                <Grid item xs={6} md={4}>
                  <Input
                    label={showLabel ? "Skin Quantity" : undefined}
                    name={`skinsLots[${i}]quantity`}
                    type="number"
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <Input
                    label={showLabel ? "Skin Price" : undefined}
                    name={`skinsLots[${i}]unitPrice`}
                    beforeSymbol="$"
                    type="number"
                    decimal
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Input
                    label={showLabel ? "Skin Value" : undefined}
                    name="skinValue"
                    beforeSymbol="$"
                    disabled
                    overrideValue={formatDecimal(skinValue / 100)}
                    align="right"
                  />
                </Grid>
                {showDelete && (
                  <Grid
                    item
                    xs={isLast ? 6 : 12}
                    md={1}
                    style={{ alignSelf: "flex-end" }}
                  >
                    <SlimSecondaryButton
                      type="button"
                      fullWidth
                      onClick={() => handleClickDelete(skinsLot, i)}
                    >
                      {isMobile ? (
                        <IconButtonLabel>
                          <FaIcon icon={faTrash} /> Delete Skins
                        </IconButtonLabel>
                      ) : (
                        "Delete"
                      )}
                    </SlimSecondaryButton>
                  </Grid>
                )}
                {isMobile && isLast && (
                  <Grid item xs={showDelete ? 6 : 12}>
                    <SlimButton
                      fullWidth
                      onClick={() =>
                        addSkinsLot(arrayHelpers, skinsLots, skinsSexId)
                      }
                      type="button"
                    >
                      <IconButtonLabel>
                        <FaIcon icon={faPlus} /> {addLineText}
                      </IconButtonLabel>
                    </SlimButton>
                  </Grid>
                )}
              </React.Fragment>
            );
          })}

          {!isMobile && (
            <Grid item xs={12}>
              <Row justifyEnd>
                <SlimButton
                  onClick={() =>
                    addSkinsLot(arrayHelpers, skinsLots, skinsSexId)
                  }
                  type="button"
                >
                  {addLineText}
                </SlimButton>
              </Row>
            </Grid>
          )}
          {isMobile && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}
        </>
      )}
    </FieldArray>
  );
};
